import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Roofline, 140 linear ft: commercial-grade C9 warm white on clips, custom-cut to length', amount: '$1,120' },
  { item: 'Wrap two front maples, trunks and main limbs to 12 ft, mini-lights', amount: '$480' },
  { item: 'Timer, outdoor cords and GFCI check; takedown in January and storage until next season', amount: '$350' },
  { item: 'Optional: two 36 in lit wreaths on the garage gables', amount: '$260' },
];

const faq = [
  {
    q: 'How do holiday lighting companies price an installation?',
    a: 'Mostly by the linear foot of roofline and by the size of the trees or shrubs wrapped, with separate lines for timers and power, wreaths and garland, and takedown and storage. Whether the customer is buying the lights or leasing them for the season changes the number, so say which on the quote.',
  },
  {
    q: 'Should I take a deposit before I buy the lights?',
    a: 'Many installers do, because custom-cut runs cannot go on another house. If you take one, put your deposit rule on the quote so the customer agrees to it when they sign, before you order.',
  },
  {
    q: 'Can customers add wreaths or tree wraps themselves?',
    a: 'Yes. Put extras like wreaths, garland or a second tree as optional lines. The customer ticks what they want on the quote page and the total updates before they sign.',
  },
  {
    q: 'Can I re-quote last year\'s customers quickly?',
    a: 'Every customer stays in your client list with the quote they signed, so next fall you can start from the same footage and options and email a fresh quote in a few minutes.',
  },
];

const page: IndustryPage = {
  slug: 'holiday-lighting',
  order: 16,
  group: 'Outdoor',
  path: '/for/holiday-lighting',
  primaryKeyword: 'christmas light installation software',
  secondaryKeywords: ['holiday lighting business software', 'christmas light installation quote', 'how to price christmas light installation'],
  title: 'Christmas Light Installation Software for Quotes | Scopie', // 57
  metaDescription:
    'Quote holiday light installs by roofline foot and tree, add wreaths as extras, collect the deposit rule before buying lights, and book takedown in January.', // 154
  h1: 'Holiday lighting quotes, signed before the first strand goes up',
  answerFirst:
    'Scopie gives holiday lighting installers a quote built the way the work is: roofline by the foot, trees by height, timers and power, then takedown and storage. Wreaths and garland are extras the homeowner can tick. Email the quote, collect a signature and your deposit terms, and book the install and the January takedown.',
  exampleQuote: { label: 'Sample holiday lighting quote', lines, total: '$2,210', depositNote: 'The deposit rule for custom-cut lights is the installer\'s own clause.' },
  blocks: [
    {
      type: 'text',
      id: 'window',
      h2: 'A six-week season decides your year',
      lead: 'Everyone wants lights up the weekend after Thanksgiving. You have four crews and one lift.',
      paragraphs: [
        'Holiday lighting is the most compressed season in the home service calendar. Design calls start in September, installs run from early November to mid-December, and takedowns fill January. A quote that waits a week in October can mean a house you never fit in.',
        'That is why the quote has to go out the same day as the design visit. In Scopie you email it from the driveway; the homeowner opens it with your business name at the top, picks the extras and signs. The install date is booked while there are still dates to give.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A front-of-house design, line by line',
      label: 'Sample quote: roofline, two trees and wreaths',
      lines,
      total: '$2,210',
      totalLabel: 'Total with the wreaths',
      note: 'Example figures. Wreaths are an optional line; without them the homeowner signs for roofline, trees and service.',
    },
    {
      type: 'list',
      id: 'design-visit',
      h2: 'What to settle at the design visit',
      style: 'checklist',
      items: [
        { text: 'Measured roofline footage, and which rooflines are lit: front only, or all sides.' },
        { text: 'Bulb type and color temperature: warm white, cool white, multicolor.' },
        { text: 'Purchase or lease: does the customer own the lights after the season?' },
        { text: 'Power: outlet locations, GFCI condition, whether a timer or smart plug is wanted.' },
        { text: 'Roof pitch and height, and whether a lift or extra crew member is needed.' },
        { text: 'Takedown window and whether you store the lights.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'How lighting installers lose money on quotes',
      style: 'mistakes',
      items: [
        { title: 'Buying lights on a verbal yes', text: 'Custom-cut strands fit one house. Get the signature and deposit terms first.' },
        { title: 'Takedown left off', text: 'Taking lights down in January is half the labor again. Price it on the same quote.' },
        { title: 'No service-call rule', text: 'A tripped breaker or a dead strand in December means a truck roll. Say what is covered.' },
        { title: 'Ownership unclear', text: 'Leased lights come back to you; purchased lights stay. Customers forget which.' },
        { title: 'Steep roofs priced like ranches', text: 'Pitch and height change crew size and time. Price them in.' },
      ],
    },
    {
      type: 'text',
      id: 'crews',
      h2: 'Install nights, crews and the lift',
      paragraphs: [
        'Signed installs go on the schedule with the crew and the lift or ladders they need, so the team can see where the lift is each night. Installers see addresses and design notes, not prices. When the house is lit, Scopie emails the invoice link to the homeowner, and the January takedown is already on the calendar.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Holiday lighting quote questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/gutter-cleaning', anchor: 'Scopie for gutter businesses', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
  ],
  cta: {
    heading: 'Fill the December calendar in October',
    text: 'Set up your footage prices, bulb options and takedown terms in Scopie, and send each design quote the same day.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
